import { $authHost, $host } from ".";

export type AppointmentData = {
  club_id: string;
  appointment_id: string;
  type: string;
  status: string;
  reason_appointment: string | null;
  arrival_status: string;
  reason_client: string | null;
  payment: {
    ticket_id: string;
    type: string;
    title: string;
  };
  service: {
    title: string;
    id: string;
    description: string;
    color: string | null;
    course: {
      id: string | null;
      title: string | null;
    };
  };
  group: {
    id: string;
    title: string;
  };
  room: {
    id: string;
    title: string;
    description: string | null;
    photo: string | null;
    capacity: number | null;
    area: number;
    pictures: Array<string>;
  };
  employee: {
    id: string;
    name: string;
    position: {
      id: string;
      title: string;
    };
  };
  capacity: number;
  start_date: string;
  end_date: string;
  duration: number;
  commercial: boolean;
  pre_entry: boolean;
  marketing_badges: Array<string>;
  hide_cancel_button: boolean;
  course_activity: boolean;
};

export type GetAppointmentsResponse = {
  result: boolean;
  data: AppointmentData[];
  pagination_response: {
    requested_offset: number;
    page_size: number;
    total_results: number;
  };
};

const buildAppointmentsParams = (type: string, activeOnly: boolean): Record<string, string> => {
  const params: Record<string, string> = { type };
  if (activeOnly) {
    params.statuses = '"planned","temporarily_reserved_need_payment"';
  }
  return params;
};

// Тянем и персональные, и групповые занятия (type=classes). 1С-API не умеет отдавать
// оба типа одним запросом, поэтому делаем параллельно через allSettled: если групповой
// эндпоинт упадёт, персональные (основной легаси-функционал) всё равно отрендерятся.
export async function getAppointments(usertoken: string, activeOnly: boolean = true) {
  const [personalRes, groupRes] = await Promise.allSettled([
    $authHost.request<GetAppointmentsResponse>({
      url: "/appointments",
      params: buildAppointmentsParams("personal", activeOnly),
      headers: { usertoken },
    }),
    $authHost.request<GetAppointmentsResponse>({
      url: "/appointments",
      params: buildAppointmentsParams("group", activeOnly),
      headers: { usertoken },
    }),
  ]);

  // Если персональные упали — это старый критичный функционал, пробрасываем ошибку.
  if (personalRes.status === "rejected") throw personalRes.reason;

  const personalData = personalRes.value.data.data ?? [];
  const groupData = groupRes.status === "fulfilled" ? (groupRes.value.data.data ?? []) : [];

  // 1С-API игнорирует фильтр `type` и возвращает personal-записи в ответе
  // `type=group` тоже, поэтому без дедупа каждая личная запись рендерится дважды.
  const seen = new Set<string>();
  const merged: AppointmentData[] = [];
  for (const item of [...personalData, ...groupData]) {
    if (seen.has(item.appointment_id)) continue;
    seen.add(item.appointment_id);
    merged.push(item);
  }
  merged.sort((a, b) => a.start_date.localeCompare(b.start_date));

  return {
    ...personalRes.value,
    data: {
      result: personalRes.value.data.result,
      data: merged,
      pagination_response: personalRes.value.data.pagination_response,
    },
  };
}

export async function deleteClasses(usertoken: string, appointment_id: string) {
  return $authHost.delete("/appointment", {
    params: { appointment_id },
    headers: { usertoken },
  });
}

export async function getAppointmentInfo(club_id: string, appointment_id: string) {
  const response = await $host.get("/appointment", {
    params: { club_id, appointment_id },
  });
  return response.data.data;
}
